import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Mail, Lock, Eye, EyeOff, User, X,
  Loader2, CheckCircle, AlertCircle, ArrowLeft, RefreshCw,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { saveUserSession } from '../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
// /api/auth/google does not exist yet (404 on ivisora.co): keep the button off until it does.
const googleAuthEnabled = () => process.env.REACT_APP_GOOGLE_AUTH_ENABLED === 'true';

/* ─── Keyframes ─────────────────────────────────────────────────────────────── */
const STYLES = `
  /* Left panel floating shapes */
  @keyframes lp-float {
    0%,100% { transform: translateY(0) rotate(var(--r,0deg)); }
    50%     { transform: translateY(-18px) rotate(var(--r,0deg)); }
  }
  @keyframes lp-blob {
    0%,100% { transform: translate(0,0) scale(1); }
    35%     { transform: translate(14px,-22px) scale(1.06); }
    68%     { transform: translate(-10px,12px) scale(0.96); }
  }

  /* Preview canvas sequence (8s per cycle, all start at 0%) */
  @keyframes pv-canvas {
    0%,3%  { opacity:0; transform:translateY(16px) scale(0.9); }
    12%    { opacity:1; transform:translateY(0) scale(1); }
    76%    { opacity:1; transform:translateY(0) scale(1); }
    86%    { opacity:0; transform:translateY(-6px) scale(0.97); }
    100%   { opacity:0; }
  }
  @keyframes pv-el2 {
    0%,16%  { opacity:0; transform:translateX(-12px); }
    24%     { opacity:1; transform:translateX(0); }
    76%     { opacity:1; }
    84%     { opacity:0; }
    100%    { opacity:0; }
  }
  @keyframes pv-el3 {
    0%,26%  { opacity:0; transform:translateY(8px); }
    34%     { opacity:1; transform:translateY(0); }
    76%     { opacity:1; }
    84%     { opacity:0; }
    100%    { opacity:0; }
  }
  @keyframes pv-el4 {
    0%,35%  { opacity:0; transform:translateY(6px); }
    43%     { opacity:1; transform:translateY(0); }
    76%     { opacity:1; }
    84%     { opacity:0; }
    100%    { opacity:0; }
  }
  @keyframes pv-el5 {
    0%,43%  { opacity:0; transform:scale(0.65); }
    50%     { opacity:1; transform:scale(1.08); }
    53%     { transform:scale(1); }
    76%     { opacity:1; }
    84%     { opacity:0; }
    100%    { opacity:0; }
  }
  @keyframes pv-el6 {
    0%,50%  { opacity:0; transform:scale(0.8) translateY(6px); }
    58%     { opacity:1; transform:scale(1) translateY(0); }
    76%     { opacity:1; }
    84%     { opacity:0; }
    100%    { opacity:0; }
  }
  @keyframes pv-float {
    0%,100% { transform:translateY(0); }
    50%     { transform:translateY(-7px); }
  }

  /* Modal entrance */
  @keyframes auth-overlay-in {
    from { opacity:0; }
    to   { opacity:1; }
  }
  @keyframes auth-card-in {
    from { opacity:0; transform:scale(0.9) translateY(20px); }
    to   { opacity:1; transform:scale(1) translateY(0); }
  }

  /* Form feedback */
  @keyframes auth-error-in {
    from { opacity:0; transform:translateY(-6px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes auth-shake {
    0%,100% { transform:translateX(0); }
    20%     { transform:translateX(-7px); }
    40%     { transform:translateX(7px); }
    60%     { transform:translateX(-4px); }
    80%     { transform:translateX(4px); }
  }
  @keyframes auth-success-pop {
    0%   { transform:scale(0.4); opacity:0; }
    60%  { transform:scale(1.2); opacity:1; }
    100% { transform:scale(1);   opacity:1; }
  }
  @keyframes otp-digit-pop {
    0%   { transform:scale(0.7) translateY(6px); opacity:0; }
    65%  { transform:scale(1.1) translateY(-2px); }
    100% { transform:scale(1)   translateY(0); opacity:1; }
  }
  @keyframes otp-cursor {
    0%,100% { opacity:1; }
    50%     { opacity:0; }
  }
`;

/* ─── Google SVG icon ───────────────────────────────────────────────────────── */
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

/* ─── Left panel floating shapes (white/cyan, low opacity) ──────────────────── */
const LP_SHAPES = [
  { id:1,  t:'circle',   x:'6%',  y:'10%', s:70, op:.08, dur:9,  del:0,   r:0   },
  { id:2,  t:'circle',   x:'85%', y:'18%', s:45, op:.07, dur:7,  del:1.2, r:0   },
  { id:3,  t:'circle',   x:'10%', y:'72%', s:55, op:.06, dur:11, del:2,   r:0   },
  { id:4,  t:'circle',   x:'88%', y:'70%', s:35, op:.08, dur:8,  del:0.5, r:0   },
  { id:5,  t:'triangle', x:'75%', y:'8%',  s:36, op:.10, dur:13, del:1.5, r:0   },
  { id:6,  t:'triangle', x:'18%', y:'40%', s:28, op:.08, dur:9,  del:3,   r:180 },
  { id:7,  t:'rect',     x:'70%', y:'82%', s:28, op:.09, dur:10, del:1,   r:20  },
  { id:8,  t:'rect',     x:'3%',  y:'35%', s:22, op:.07, dur:7,  del:2.5, r:-25 },
  { id:9,  t:'ring',     x:'80%', y:'50%', s:54, op:.07, dur:16, del:0,   r:0   },
  { id:10, t:'ring',     x:'35%', y:'2%',  s:40, op:.06, dur:12, del:2,   r:0   },
  { id:11, t:'ring',     x:'22%', y:'85%', s:48, op:.07, dur:10, del:1,   r:0   },
  { id:12, t:'hex',      x:'55%', y:'88%', s:34, op:.08, dur:11, del:1.8, r:0   },
  { id:13, t:'hex',      x:'92%', y:'40%', s:28, op:.07, dur:8,  del:3.5, r:30  },
  { id:14, t:'dots',     x:'60%', y:'20%', op:.12, dur:9, del:1             },
  { id:15, t:'dots',     x:'8%',  y:'58%', op:.10, dur:11, del:0            },
  { id:16, t:'line',     x:'40%', y:'92%', w:100, h:3, op:.10, dur:14, del:0.5, r:-20 },
  { id:17, t:'line',     x:'78%', y:'30%', w:80,  h:2, op:.08, dur:10, del:2,   r:30  },
];

const LP_BLOBS = [
  { id:'a', x:'0%',  y:'0%',  s:280, dur:14, del:0   },
  { id:'b', x:'55%', y:'40%', s:320, dur:18, del:2   },
  { id:'c', x:'20%', y:'65%', s:240, dur:12, del:1   },
];

const LeftPanelBg = ({ parallax }) => (
  <div
    className="absolute inset-0 overflow-hidden pointer-events-none"
    style={{
      transform: `translate(${parallax.x * 0.2}px, ${parallax.y * 0.15}px)`,
      transition: 'transform 0.12s linear',
    }}
  >
    {/* Blobs */}
    {LP_BLOBS.map(b => (
      <div key={b.id} style={{
        position: 'absolute', left: b.x, top: b.y,
        width: b.s, height: b.s, borderRadius: '50%',
        background: 'rgba(255,255,255,0.07)',
        filter: 'blur(60px)',
        animation: `lp-blob ${b.dur}s ease-in-out ${b.del}s infinite`,
      }} />
    ))}

    {/* Shapes */}
    {LP_SHAPES.map(el => {
      const anim = `lp-float ${el.dur}s ease-in-out ${el.del}s infinite`;
      const base = {
        position: 'absolute', left: el.x, top: el.y,
        animation: anim, '--r': `${el.r || 0}deg`, opacity: el.op,
      };
      const c = 'rgba(255,255,255,1)';
      if (el.t === 'circle') return (
        <div key={el.id} style={{ ...base, width: el.s, height: el.s, borderRadius: '50%', background: c }} />
      );
      if (el.t === 'triangle') return (
        <div key={el.id} style={{ ...base, width: el.s, height: el.s, background: c,
          clipPath: 'polygon(50% 0%,0% 100%,100% 100%)', transform: `rotate(${el.r}deg)` }} />
      );
      if (el.t === 'rect') return (
        <div key={el.id} style={{ ...base, width: el.s, height: el.s, borderRadius: 5, background: c, transform: `rotate(${el.r}deg)` }} />
      );
      if (el.t === 'ring') return (
        <div key={el.id} style={{ ...base, width: el.s, height: el.s, borderRadius: '50%', border: '2px solid rgba(255,255,255,1)', background: 'transparent' }} />
      );
      if (el.t === 'hex') return (
        <div key={el.id} style={{ ...base, width: el.s, height: el.s, background: c,
          clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)', transform: `rotate(${el.r || 0}deg)` }} />
      );
      if (el.t === 'dots') return (
        <div key={el.id} style={{ ...base, display: 'flex', gap: 5 }}>
          {[0,1,2,3,4].map(i => (
            <div key={i} style={{ width: i===2?12:8, height: i===2?12:8, borderRadius: '50%', background: c, opacity: i%2===0?1:0.55 }} />
          ))}
        </div>
      );
      if (el.t === 'line') return (
        <div key={el.id} style={{ ...base, width: el.w, height: el.h, borderRadius: el.h, background: c, transform: `rotate(${el.r}deg)` }} />
      );
      return null;
    })}
  </div>
);

/* ─── Animated design preview (CSS keyframe sequence) ───────────────────────── */
const DesignPreview = () => {
  const DUR = '8s';
  const s = (name, extra = {}) => ({
    animation: `${name} ${DUR} ease-in-out infinite both`,
    ...extra,
  });
  return (
    <div style={{ position: 'relative', width: 220 }}>
      {/* Floating badge — top right */}
      <div style={{
        position: 'absolute', top: -16, right: -16, zIndex: 20,
        background: 'rgba(255,255,255,0.18)',
        border: '1px solid rgba(255,255,255,0.3)',
        backdropFilter: 'blur(10px)',
        borderRadius: 20, padding: '5px 12px',
        fontSize: 11, fontWeight: 700, color: 'white',
        ...s('pv-el6'),
      }}>
        ✦ New Design
      </div>

      {/* Canvas card */}
      <div style={{
        ...s('pv-canvas'),
        animation: `pv-canvas ${DUR} ease-in-out infinite both, pv-float 4s ease-in-out infinite`,
        background: 'white',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
      }}>
        {/* Card header bar */}
        <div style={{
          background: 'linear-gradient(90deg, #1e40af, #2563eb)',
          padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 6,
          ...s('pv-el2'),
        }}>
          {['#fff','rgba(255,255,255,0.6)','rgba(255,255,255,0.4)'].map((c,i) => (
            <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: c }} />
          ))}
          <div style={{
            marginLeft: 8, fontSize: 10, fontWeight: 800,
            color: 'white', letterSpacing: '0.05em',
          }}>
            IVISORA
          </div>
        </div>

        <div style={{ padding: '14px 14px 16px' }}>
          {/* Image placeholder */}
          <div style={{
            height: 80, borderRadius: 10, marginBottom: 12,
            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 50%, #93c5fd 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative', overflow: 'hidden',
            ...s('pv-el2'),
          }}>
            {/* abstract photo grid lines */}
            <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 2, padding: 6 }}>
              {[0,1,2,3].map(i => (
                <div key={i} style={{ borderRadius: 4, background: i%2===0 ? 'rgba(37,99,235,0.2)' : 'rgba(56,189,248,0.25)' }} />
              ))}
            </div>
            <div style={{
              position: 'relative', zIndex: 1, fontSize: 18,
              background: 'linear-gradient(135deg, #2563eb, #38bdf8)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              fontWeight: 900,
            }}>
              ◈
            </div>
          </div>

          {/* Title */}
          <div style={{
            height: 12, borderRadius: 6, marginBottom: 7,
            background: 'linear-gradient(90deg, #1e293b 0%, #475569 100%)',
            width: '80%',
            ...s('pv-el3'),
          }} />

          {/* Subtitle lines */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
            {[100, 70].map((w, i) => (
              <div key={i} style={{
                height: 7, borderRadius: 4, background: '#e2e8f0', width: `${w}%`,
                ...s('pv-el4'),
              }} />
            ))}
          </div>

          {/* Color palette dots */}
          <div style={{
            display: 'flex', gap: 5, marginBottom: 12, alignItems: 'center',
            ...s('pv-el5'),
          }}>
            {['#2563eb','#38bdf8','#0ea5e9','#93c5fd','#dbeafe'].map((c,i) => (
              <div key={i} style={{
                width: i===0?14:10, height: i===0?14:10,
                borderRadius: '50%', background: c,
                boxShadow: i===0 ? `0 2px 8px ${c}80` : 'none',
              }} />
            ))}
            <div style={{ fontSize: 9, color: '#94a3b8', marginLeft: 4, fontWeight: 600 }}>+12</div>
          </div>

          {/* CTA button */}
          <div style={{
            height: 28, borderRadius: 8,
            background: 'linear-gradient(90deg, #2563eb, #38bdf8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            ...s('pv-el6'),
          }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: 'white', letterSpacing: '0.04em' }}>
              GET STARTED →
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Field input with animated focus glow ──────────────────────────────────── */
const FieldInput = ({ type, label, icon, value, onChange, placeholder, required, suffix, autoFocus }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && (
        <label style={{
          display: 'block', fontSize: 11, fontWeight: 700,
          color: focused ? '#2563eb' : '#64748b',
          marginBottom: 5, letterSpacing: '0.06em',
          transition: 'color 0.18s',
          textTransform: 'uppercase',
        }}>
          {label}
        </label>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', height: 44,
        borderRadius: 11, border: `1.5px solid ${focused ? '#2563eb' : '#e2e8f0'}`,
        background: focused ? '#eff6ff' : '#f8fafc',
        boxShadow: focused ? '0 0 0 3px rgba(37,99,235,0.12)' : 'none',
        paddingLeft: 12, paddingRight: suffix ? 8 : 14, gap: 9,
        transition: 'border-color 0.18s, box-shadow 0.18s, background 0.18s',
      }}>
        <span style={{ color: focused ? '#2563eb' : '#94a3b8', flexShrink: 0, display: 'flex', alignItems: 'center', transition: 'color 0.18s' }}>
          {React.cloneElement(icon, { style: { width: 15, height: 15 } })}
        </span>
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
          autoFocus={autoFocus}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 14, color: '#0f172a' }}
        />
        {suffix && <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{suffix}</span>}
      </div>
    </div>
  );
};

/* ─── OTP 6-digit input ──────────────────────────────────────────────────────── */
const OtpInput = ({ value, onChange }) => {
  const refs = useRef([]);
  const digits = value.split('').concat(Array(6).fill('')).slice(0, 6);

  const handleChange = (idx, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);
    const arr = [...digits];
    arr[idx] = digit;
    onChange(arr.join(''));
    if (digit && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      if (digits[idx]) {
        const arr = [...digits]; arr[idx] = '';
        onChange(arr.join(''));
      } else if (idx > 0) {
        refs.current[idx - 1]?.focus();
      }
    }
    if (e.key === 'ArrowLeft' && idx > 0) refs.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    const focusIdx = Math.min(pasted.length, 5);
    refs.current[focusIdx]?.focus();
  };

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
      {digits.map((d, i) => {
        const filled = d !== '';
        return (
          <input
            key={i}
            ref={el => refs.current[i] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            onChange={e => handleChange(i, e)}
            onKeyDown={e => handleKeyDown(i, e)}
            onPaste={handlePaste}
            autoFocus={i === 0}
            style={{
              width: 44, height: 52,
              textAlign: 'center',
              fontSize: 22, fontWeight: 800,
              color: filled ? '#2563eb' : '#94a3b8',
              background: filled ? '#eff6ff' : '#f8fafc',
              border: `2px solid ${filled ? '#2563eb' : '#e2e8f0'}`,
              borderRadius: 12,
              outline: 'none',
              transition: 'all 0.18s',
              animation: filled ? 'otp-digit-pop 0.3s cubic-bezier(0.34,1.56,0.64,1)' : 'none',
              boxShadow: filled ? '0 0 0 3px rgba(37,99,235,0.12)' : 'none',
              cursor: 'text',
            }}
          />
        );
      })}
    </div>
  );
};

/* ─── Submit button ──────────────────────────────────────────────────────────── */
const SubmitBtn = ({ state, idleLabel, loadingLabel, successLabel, disabled }) => {
  const isSuccess = state === 'success';
  const isLoading = state === 'loading';
  const isDisabled = disabled || state !== 'idle';
  return (
    <button
      type="submit"
      disabled={isDisabled}
      style={{
        width: '100%', height: 46, borderRadius: 11,
        background: isSuccess
          ? 'linear-gradient(to right, #16a34a, #15803d)'
          : 'linear-gradient(135deg, #1e40af, #2563eb, #38bdf8)',
        color: 'white', fontWeight: 700, fontSize: 14,
        border: 'none', cursor: isDisabled ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        transition: 'background 0.3s, transform 0.12s, box-shadow 0.18s',
        boxShadow: isSuccess
          ? '0 4px 18px rgba(22,163,74,0.35)'
          : '0 4px 18px rgba(37,99,235,0.35)',
        opacity: isDisabled && !isLoading && !isSuccess ? 0.6 : 1,
        letterSpacing: '0.01em',
      }}
      onMouseEnter={e => {
        if (!isDisabled) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 10px 30px rgba(37,99,235,0.45)';
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = isSuccess
          ? '0 4px 18px rgba(22,163,74,0.35)'
          : '0 4px 18px rgba(37,99,235,0.35)';
      }}
    >
      {isSuccess ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, animation: 'auth-success-pop 0.45s ease-out' }}>
          <CheckCircle style={{ width: 18, height: 18 }} /> {successLabel}
        </span>
      ) : isLoading ? (
        <><Loader2 style={{ width: 17, height: 17 }} className="animate-spin" /> {loadingLabel}</>
      ) : idleLabel}
    </button>
  );
};

/* ─── Error banner ───────────────────────────────────────────────────────────── */
const ErrorBanner = ({ msg }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: 9,
    padding: '10px 14px', borderRadius: 10,
    background: 'rgba(239,68,68,0.07)',
    border: '1.5px solid rgba(239,68,68,0.2)',
    color: '#dc2626', fontSize: 13,
    animation: 'auth-error-in 0.28s ease-out both',
  }}>
    <AlertCircle style={{ width: 14, height: 14, flexShrink: 0, marginTop: 1 }} />
    {msg}
  </div>
);

/* ─── "OR" divider ───────────────────────────────────────────────────────────── */
const OrDivider = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em' }}>OR</span>
    <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
  </div>
);

/* ─── Eye toggle button ──────────────────────────────────────────────────────── */
const EyeToggle = ({ show, onToggle }) => (
  <button
    type="button" onClick={onToggle}
    style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'color 0.15s' }}
    onMouseEnter={e => e.currentTarget.style.color = '#2563eb'}
    onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
  >
    {show ? <EyeOff style={{ width: 15, height: 15 }} /> : <Eye style={{ width: 15, height: 15 }} />}
  </button>
);

/* ═══════════════════════════════════════════════════════════════════════════════
   AuthModal — main component
═══════════════════════════════════════════════════════════════════════════════ */
const AuthModal = ({ open, defaultTab = 'login', onClose, onAuthSuccess }) => {
  const { toast } = useToast();

  const [authTab, setAuthTab]       = useState(defaultTab);
  const [parallax, setParallax]     = useState({ x: 0, y: 0 });

  /* ── Login state ── */
  const [loginEmail, setLoginEmail]   = useState('');
  const [loginPw, setLoginPw]         = useState('');
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [loginState, setLoginState]   = useState('idle');
  const [loginError, setLoginError]   = useState('');
  const [loginShakeKey, setLoginShakeKey] = useState(0);

  /* ── Signup state ── */
  const [signupStep, setSignupStep]   = useState('form'); // form | otp
  const [signupName, setSignupName]   = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPw, setSignupPw]       = useState('');
  const [signupCpw, setSignupCpw]     = useState('');
  const [showSignupPw, setShowSignupPw] = useState(false);
  const [signupState, setSignupState] = useState('idle');
  const [signupError, setSignupError] = useState('');
  const [signupShakeKey, setSignupShakeKey] = useState(0);
  const [otp, setOtp]                 = useState('');
  const [otpState, setOtpState]       = useState('idle');
  const [otpError, setOtpError]       = useState('');
  const [otpShakeKey, setOtpShakeKey] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  // Sync tab when modal opens
  useEffect(() => {
    if (open) {
      setAuthTab(defaultTab);
      setLoginError(''); setSignupError(''); setOtpError('');
      setSignupStep('form');
      setOtp('');
    }
  }, [open, defaultTab]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleParallax = useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setParallax({
      x: ((e.clientX - r.left - r.width / 2) / r.width) * 30,
      y: ((e.clientY - r.top - r.height / 2) / r.height) * 20,
    });
  }, []);

  const switchTab = (tab) => {
    setAuthTab(tab); setLoginError(''); setSignupError('');
    setSignupStep('form'); setOtp('');
  };

  /* ── Google login ── */
  const handleGoogleLogin = () => {
    if (!API_URL) {
      toast({ title: 'Backend not configured', description: 'Add REACT_APP_BACKEND_URL to your .env', variant: 'destructive' });
      return;
    }
    window.location.href = `${API_URL}/api/auth/google`;
  };

  /* ── Login ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!API_URL) { setLoginError('Backend not configured.'); return; }
    setLoginState('loading');
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(
        typeof data.detail === 'string' ? data.detail :
        Array.isArray(data.detail) ? data.detail.map(d => d.msg || JSON.stringify(d)).join(', ') :
        'Invalid email or password.'
      );
      const userData = { id: data.user.id, name: data.user.name, email: data.user.email, plan: data.user.plan || 'free' };
      // Store the session here, where every site sign-in arrives, so it
      // exists whichever page opened this modal.
      saveUserSession(userData, data.access_token);
      setLoginState('success');
      toast({ title: 'Welcome back!', description: `Signed in as ${userData.name}` });
      setTimeout(() => {
        onAuthSuccess(userData, data.access_token);
        onClose();
        setLoginState('idle'); setLoginEmail(''); setLoginPw('');
      }, 1200);
    } catch (err) {
      setLoginError(err.message || 'Invalid email or password.');
      setLoginShakeKey(k => k + 1);
      setLoginState('idle');
    }
  };

  /* ── Signup step 1 ── */
  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError('');
    if (signupPw !== signupCpw) {
      setSignupError('Passwords do not match.');
      setSignupShakeKey(k => k + 1); return;
    }
    if (signupPw.length < 6) {
      setSignupError('Password must be at least 6 characters.'); return;
    }
    if (!API_URL) { setSignupError('Backend not configured.'); return; }
    setSignupState('loading');
    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: signupName, email: signupEmail, password: signupPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(
        typeof data.detail === 'string' ? data.detail :
        Array.isArray(data.detail) ? data.detail.map(d => d.msg || JSON.stringify(d)).join(', ') :
        'Could not create account.'
      );
      // If backend returns token directly — complete signup
      if (data.access_token) {
        const userData = { id: data.user.id, name: data.user.name, email: data.user.email, plan: data.user.plan || 'free' };
        // Store the session here, where every site sign-in arrives, so it
        // exists whichever page opened this modal.
        saveUserSession(userData, data.access_token);
        setSignupState('success');
        toast({ title: 'Account created!', description: `Welcome, ${userData.name}!` });
        setTimeout(() => {
          onAuthSuccess(userData, data.access_token);
          onClose();
          setSignupState('idle');
          setSignupName(''); setSignupEmail(''); setSignupPw(''); setSignupCpw('');
        }, 1200);
      } else {
        // OTP flow — backend sent OTP to email
        setSignupState('idle');
        setSignupStep('otp');
        startResendCooldown();
      }
    } catch (err) {
      setSignupError(err.message || 'Could not create account.');
      setSignupShakeKey(k => k + 1);
      setSignupState('idle');
    }
  };

  /* ── OTP resend cooldown ── */
  const startResendCooldown = () => {
    setResendCooldown(60);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };
  useEffect(() => () => clearInterval(cooldownRef.current), []);

  /* ── OTP verification ── */
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 6) {
      setOtpError('Please enter all 6 digits.'); return;
    }
    setOtpError('');
    setOtpState('loading');
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: signupEmail, otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(
        typeof data.detail === 'string' ? data.detail : 'Invalid verification code.'
      );
      const userData = { id: data.user.id, name: data.user.name, email: data.user.email, plan: data.user.plan || 'free' };
      // Store the session here, where every site sign-in arrives, so it
      // exists whichever page opened this modal.
      saveUserSession(userData, data.access_token);
      setOtpState('success');
      toast({ title: 'Email verified!', description: `Welcome to iVisora, ${userData.name}!` });
      setTimeout(() => {
        onAuthSuccess(userData, data.access_token);
        onClose();
        setOtpState('idle');
        setSignupName(''); setSignupEmail(''); setSignupPw(''); setSignupCpw(''); setOtp('');
        setSignupStep('form');
      }, 1200);
    } catch (err) {
      setOtpError(err.message || 'Invalid verification code.');
      setOtpShakeKey(k => k + 1);
      setOtpState('idle');
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      await fetch(`${API_URL}/api/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: signupEmail }),
      });
      startResendCooldown();
      setOtp('');
      toast({ title: 'Code resent', description: `A new code was sent to ${signupEmail}` });
    } catch {
      toast({ title: 'Failed to resend', variant: 'destructive' });
    }
  };

  if (!open) return null;

  /* ═══ Render ═══ */
  return (
    <>
      <style>{STYLES}</style>
      {/* ── Full-screen overlay ── */}
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{
          background: 'rgba(15,23,64,0.72)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          animation: 'auth-overlay-in 0.25s ease-out both',
        }}
        onMouseMove={handleParallax}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* ── Modal card ── */}
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: 880,
            maxHeight: '94vh',
            borderRadius: 24,
            overflow: 'hidden',
            display: 'flex',
            animation: 'auth-card-in 0.4s cubic-bezier(0.22,1,0.36,1) both',
            boxShadow: '0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)',
          }}
        >
          {/* ════════════════════════════════════════
              LEFT PANEL — animated design preview
          ════════════════════════════════════════ */}
          <div
            className="hidden md:flex"
            style={{
              flex: '0 0 52%',
              background: 'linear-gradient(145deg, #0c1445 0%, #1a2a6c 45%, #1d4ed8 100%)',
              position: 'relative',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px 40px',
              overflow: 'hidden',
            }}
          >
            <LeftPanelBg parallax={parallax} />

            {/* Logo top-left */}
            <div style={{
              position: 'absolute', top: 24, left: 24,
              display: 'flex', alignItems: 'center', gap: 10,
              zIndex: 10,
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: 'linear-gradient(135deg, #2563eb, #38bdf8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(37,99,235,0.5)',
              }}>
                <span style={{ fontSize: 16, color: 'white', fontWeight: 900 }}>D</span>
              </div>
              <span style={{ fontSize: 15, fontWeight: 800, color: 'white', letterSpacing: '-0.2px' }}>iVisora</span>
            </div>

            {/* Center content */}
            <div style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
              <DesignPreview />
              <div style={{ marginTop: 40 }}>
                <h2 style={{
                  fontSize: 24, fontWeight: 900, color: 'white',
                  letterSpacing: '-0.5px', lineHeight: 1.25, marginBottom: 10,
                }}>
                  Design your world
                </h2>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.65, maxWidth: 260, margin: '0 auto' }}>
                  Create stunning designs with powerful tools, templates, and real-time collaboration.
                </p>
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════
              RIGHT PANEL — auth forms
          ════════════════════════════════════════ */}
          <div style={{
            flex: 1,
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}>
            {/* Top bar: logo (mobile only) + close */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '20px 28px 0',
            }}>
              <div className="flex md:hidden items-center gap-2">
                <div style={{
                  width: 30, height: 30, borderRadius: 9,
                  background: 'linear-gradient(135deg, #1e40af, #38bdf8)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: 14, color: 'white', fontWeight: 900 }}>D</span>
                </div>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>iVisora</span>
              </div>
              <div className="hidden md:block" />
              <button
                onClick={onClose}
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  border: '1.5px solid #e2e8f0', background: 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#94a3b8', cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#475569'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
              >
                <X style={{ width: 15, height: 15 }} />
              </button>
            </div>

            {/* Tab switcher */}
            <div style={{ padding: '16px 28px 0' }}>
              <div style={{
                position: 'relative', display: 'flex',
                background: '#f1f5f9', borderRadius: 12, padding: 4,
              }}>
                <div style={{
                  position: 'absolute', top: 4, bottom: 4,
                  left: authTab === 'login' ? 4 : 'calc(50% + 2px)',
                  width: 'calc(50% - 6px)',
                  background: 'white',
                  borderRadius: 9,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'left 0.32s cubic-bezier(0.34,1.2,0.64,1)',
                }} />
                {[{ key: 'login', label: 'Sign In' }, { key: 'signup', label: 'Create Account' }].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => switchTab(key)}
                    style={{
                      flex: 1, padding: '9px 0', fontSize: 13, fontWeight: 700,
                      color: authTab === key ? '#0f172a' : '#94a3b8',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      position: 'relative', zIndex: 1, transition: 'color 0.22s', borderRadius: 9,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Sliding forms ── */}
            <div style={{ overflow: 'hidden', flex: 1 }}>
              <div style={{
                display: 'flex', width: '200%',
                transform: authTab === 'login' ? 'translateX(0)' : 'translateX(-50%)',
                transition: 'transform 0.42s cubic-bezier(0.22,1,0.36,1)',
                alignItems: 'flex-start',
              }}>

                {/* ══ LOGIN FORM ══ */}
                <div style={{ width: '50%' }}>
                  <form
                    key={`login-${loginShakeKey}`}
                    onSubmit={handleLogin}
                    style={{
                      padding: '20px 28px 28px',
                      display: 'flex', flexDirection: 'column', gap: 14,
                      animation: loginShakeKey > 0 ? 'auth-shake 0.48s ease-in-out' : undefined,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 21, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px' }}>
                        Welcome back
                      </div>
                      <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
                        Sign in to continue
                      </div>
                    </div>

                    {loginError && <ErrorBanner msg={loginError} />}

                    {/* Google — hidden until a real OAuth flow exists (REACT_APP_GOOGLE_AUTH_ENABLED=true) */}
                    {googleAuthEnabled() && (
                      <>
                      <button
                        type="button"
                        onClick={handleGoogleLogin}
                        style={{
                          width: '100%', height: 44, borderRadius: 11,
                          border: '1.5px solid #e2e8f0', background: 'white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                          fontSize: 14, fontWeight: 600, color: '#374151',
                          cursor: 'pointer', transition: 'all 0.18s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
                      >
                        <GoogleIcon /> Continue with Google
                      </button>

                      <OrDivider />
                    </>
                    )}

                    <FieldInput
                      type="email" label="Email" icon={<Mail />}
                      value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                      placeholder="you@example.com" required
                    />
                    <FieldInput
                      type={showLoginPw ? 'text' : 'password'} label="Password" icon={<Lock />}
                      value={loginPw} onChange={e => setLoginPw(e.target.value)}
                      placeholder="••••••••" required
                      suffix={<EyeToggle show={showLoginPw} onToggle={() => setShowLoginPw(v => !v)} />}
                    />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -6 }}>
                      <button type="button"
                        style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                        onMouseEnter={e => e.currentTarget.style.color = '#1d4ed8'}
                        onMouseLeave={e => e.currentTarget.style.color = '#2563eb'}
                      >
                        Forgot password?
                      </button>
                    </div>

                    <SubmitBtn state={loginState} idleLabel="Sign In" loadingLabel="Signing in…" successLabel="Signed in!" />

                    <p style={{ textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
                      No account?{' '}
                      <button type="button" onClick={() => switchTab('signup')}
                        style={{ color: '#2563eb', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                        onMouseEnter={e => e.currentTarget.style.color = '#1d4ed8'}
                        onMouseLeave={e => e.currentTarget.style.color = '#2563eb'}
                      >
                        Create one free
                      </button>
                    </p>
                  </form>
                </div>

                {/* ══ SIGNUP FORM ══ */}
                <div style={{ width: '50%' }}>
                  {signupStep === 'form' ? (
                    <form
                      key={`signup-${signupShakeKey}`}
                      onSubmit={handleSignup}
                      style={{
                        padding: '20px 28px 28px',
                        display: 'flex', flexDirection: 'column', gap: 12,
                        animation: signupShakeKey > 0 ? 'auth-shake 0.48s ease-in-out' : undefined,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 21, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px' }}>
                          Start creating
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
                          Free forever. No credit card needed.
                        </div>
                      </div>

                      {signupError && <ErrorBanner msg={signupError} />}

                      {/* Google — hidden until a real OAuth flow exists (REACT_APP_GOOGLE_AUTH_ENABLED=true) */}
                      {googleAuthEnabled() && (
                        <>
                        <button
                          type="button"
                          onClick={handleGoogleLogin}
                          style={{
                            width: '100%', height: 44, borderRadius: 11,
                            border: '1.5px solid #e2e8f0', background: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                            fontSize: 14, fontWeight: 600, color: '#374151',
                            cursor: 'pointer', transition: 'all 0.18s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
                        >
                          <GoogleIcon /> Continue with Google
                        </button>

                        <OrDivider />
                        </>
                      )}

                      <FieldInput
                        type="text" label="Full Name" icon={<User />}
                        value={signupName} onChange={e => setSignupName(e.target.value)}
                        placeholder="Your name" required
                      />
                      <FieldInput
                        type="email" label="Email" icon={<Mail />}
                        value={signupEmail} onChange={e => setSignupEmail(e.target.value)}
                        placeholder="you@example.com" required
                      />
                      <FieldInput
                        type={showSignupPw ? 'text' : 'password'} label="Password" icon={<Lock />}
                        value={signupPw} onChange={e => setSignupPw(e.target.value)}
                        placeholder="Min. 6 characters" required
                        suffix={<EyeToggle show={showSignupPw} onToggle={() => setShowSignupPw(v => !v)} />}
                      />
                      <FieldInput
                        type="password" label="Confirm Password" icon={<Lock />}
                        value={signupCpw} onChange={e => setSignupCpw(e.target.value)}
                        placeholder="••••••••" required
                      />

                      <p style={{ fontSize: 11, color: '#94a3b8' }}>
                        By signing up you agree to our{' '}
                        <span style={{ color: '#2563eb', cursor: 'pointer' }}>Terms</span> and{' '}
                        <span style={{ color: '#2563eb', cursor: 'pointer' }}>Privacy Policy</span>.
                      </p>

                      <SubmitBtn state={signupState} idleLabel="Create Account" loadingLabel="Creating…" successLabel="Account created!" />

                      <p style={{ textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
                        Already have an account?{' '}
                        <button type="button" onClick={() => switchTab('login')}
                          style={{ color: '#2563eb', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                          onMouseEnter={e => e.currentTarget.style.color = '#1d4ed8'}
                          onMouseLeave={e => e.currentTarget.style.color = '#2563eb'}
                        >
                          Sign in
                        </button>
                      </p>
                    </form>

                  ) : (
                    /* ── OTP VERIFICATION STEP ── */
                    <form
                      key={`otp-${otpShakeKey}`}
                      onSubmit={handleVerifyOtp}
                      style={{
                        padding: '20px 28px 28px',
                        display: 'flex', flexDirection: 'column', gap: 16,
                        animation: otpShakeKey > 0 ? 'auth-shake 0.48s ease-in-out' : undefined,
                      }}
                    >
                      {/* Back button */}
                      <button
                        type="button"
                        onClick={() => { setSignupStep('form'); setOtp(''); setOtpError(''); }}
                        style={{
                          alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 5,
                          fontSize: 13, fontWeight: 600, color: '#64748b',
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: 0, transition: 'color 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = '#2563eb'}
                        onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
                      >
                        <ArrowLeft style={{ width: 14, height: 14 }} /> Back
                      </button>

                      {/* Header */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          width: 60, height: 60, borderRadius: 18,
                          background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          margin: '0 auto 16px',
                          fontSize: 28,
                        }}>
                          📧
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.4px', marginBottom: 6 }}>
                          Check your email
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                          We sent a 6-digit code to<br />
                          <strong style={{ color: '#0f172a' }}>{signupEmail}</strong>
                        </div>
                      </div>

                      {otpError && <ErrorBanner msg={otpError} />}

                      {/* OTP input */}
                      <div>
                        <label style={{
                          display: 'block', textAlign: 'center',
                          fontSize: 11, fontWeight: 700, color: '#64748b',
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                          marginBottom: 12,
                        }}>
                          Verification Code
                        </label>
                        <OtpInput value={otp} onChange={setOtp} />
                      </div>

                      <SubmitBtn
                        state={otpState}
                        idleLabel="Verify Email"
                        loadingLabel="Verifying…"
                        successLabel="Verified!"
                        disabled={otp.length < 6}
                      />

                      {/* Resend */}
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: 13, color: '#94a3b8' }}>Didn't receive it? </span>
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          disabled={resendCooldown > 0}
                          style={{
                            fontSize: 13, fontWeight: 700,
                            color: resendCooldown > 0 ? '#94a3b8' : '#2563eb',
                            background: 'none', border: 'none',
                            cursor: resendCooldown > 0 ? 'default' : 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            transition: 'color 0.15s',
                          }}
                          onMouseEnter={e => { if (!resendCooldown) e.currentTarget.style.color = '#1d4ed8'; }}
                          onMouseLeave={e => { if (!resendCooldown) e.currentTarget.style.color = '#2563eb'; }}
                        >
                          <RefreshCw style={{ width: 13, height: 13 }} />
                          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                        </button>
                      </div>

                      <p style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>
                        Check your spam folder if you don't see it in your inbox.
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthModal;
